import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Output } from '@angular/core';
import { combineLatest, map, Observable, takeUntil } from 'rxjs';
import { LIB_CONFIG, LibConfig } from '../../lib.config';
import { BildungsangeboteService } from '../../services/bildungsangebote.service';
import { FilterAggregationsService } from '../../services/filter-aggregations.service';
import { LoaderService } from '../../services/loader.service';
import { IOpenGraphService, OPEN_GRAPH_SERVICE } from '../../services/open-graph.service';
import { BerufeService } from '../../services/suchseite/berufe.service';
import { PaginationService } from '../../services/suchseite/pagination.service';
import { SuchwortService } from '../../services/suchseite/suchwort.service';
import { CleanupService } from '../../shared/utils/cleanup.service';
import { KompaktAnsichtComponent } from './ansichten/kompakt-ansicht/kompakt-ansicht.component';
import { BerufeEmptyStateComponent } from './berufe-empty-state/berufe-empty-state.component';
import { MobileShowFilterButtonComponent } from './mobile-show-filter-button/mobile-show-filter-button.component';
import { RequiredInputsStateComponent } from './required-inputs-state/required-inputs-state.component';
import { SuchergebnisEmptyStateComponent } from './suchergebnis-empty-state/suchergebnis-empty-state.component';

@Component({
  selector: 'now-wbsuche-lib-suchergebnis',
  templateUrl: './suchergebnis.component.html',
  providers: [CleanupService],
  styleUrls: ['suchergebnis.component.scss'],
  imports: [
    MobileShowFilterButtonComponent,
    RequiredInputsStateComponent,
    BerufeEmptyStateComponent,
    SuchergebnisEmptyStateComponent,
    KompaktAnsichtComponent,
    AsyncPipe
  ]
})
export class SuchergebnisComponent {
  paginationService = inject(PaginationService);
  private readonly loaderService = inject(LoaderService);
  private readonly suchwortService = inject(SuchwortService);
  private readonly cleanupService = inject(CleanupService);
  private readonly bildungsangeboteService = inject(BildungsangeboteService);
  private readonly aggregationService = inject(FilterAggregationsService);
  berufeService = inject(BerufeService);
  libConfig = inject<LibConfig>(LIB_CONFIG);
  private readonly openGraphService = inject<IOpenGraphService>(OPEN_GRAPH_SERVICE, {
    optional: true
  });

  @Output()
  terminSelected = new EventEmitter<string>();

  public bildungsAngeboteLoading$: Observable<boolean> =
    this.loaderService.bildungsangeboteLoading$;
  public showRequiredInputState$ = this.suchwortService.showRequiredInputState$;
  public hasMoreHits$ = this.aggregationService.count$.pipe(map((count) => count > 10000));

  constructor() {
    this.initOpenGraph();
    this.bildungsangeboteService.bildungsAngebotePage$.subscribe();
  }

  /**
   * Aggregiert die Werte, welche für das Metaattribut 'description' benötigt werden.
   */
  private initOpenGraph(): void {
    if (this.openGraphService) {
      //Initial die Description erstellen, auch wenn z.B. noch kein Suchwort da ist.
      this.openGraphService?.setDescription(this.createDescription('', 0, false));
      combineLatest([
        this.paginationService.pageInfos$,
        this.suchwortService.suchwort$,
        this.hasMoreHits$
      ])
        .pipe(
          takeUntil(this.cleanupService.destroyed$),
          map(([pageInfos, suchwort, hasMoreHits]) => {
            return {
              totalResults: pageInfos.totalResults,
              suchwort: suchwort,
              hasMoreHits
            };
          })
        )
        .subscribe((result) => {
          const description = this.createDescription(
            result.suchwort,
            result.totalResults,
            result.hasMoreHits
          );
          this.openGraphService?.setDescription(description);
        });
    }
  }

  /**
   * Baut aus den Werten themen(Suchwort), Ort und totalResults den Wert für das Metaattribut 'description' zusammen.
   * https://ogp.me/
   */
  private createDescription(suchwort: string, totalResults: number, hasMoreHits: boolean): string {
    //Achtung in der index.html sind auch beschreibungen gesetzt. Der OpenGraph Service kann diese zur Laufzeit überschreiben.
    if (suchwort === '') {
      return `Weiterbildungen suchen und finden - bereitgestellt von einer der größten Weiterbildungsdatenbanken Deutschlands.`;
    }
    return `Entdecken Sie Weiterbildungsangebote zum Suchwort ${suchwort}. Wir haben für Sie ${
      hasMoreHits ? 'mehr als ' : ''
    }${totalResults} Suchtreffer gefunden.`;
  }
}
